import { useCallback, useMemo, useState } from "react";

import type {
  OrdersWidgetFilters,
  OrdersWidgetStatusFilter,
  OrdersWidgetStatusTab,
} from "./OrdersWidget.types";
import {
  createDefaultOrdersFilters,
  formatOrdersTimeWindow,
  getOrdersFilterDateParams,
  getOrdersFiltersValidationError,
  getSelectedStatusTab,
  hasAppliedOrdersFilters,
  normalizeOrdersFiltersForApplication,
  setOrdersStatusTab,
  toAtdOrdersStatusParam,
} from "./OrdersWidget.utils";

export interface UseOrdersFiltersControllerParams {
  defaultStatus?:
    | OrdersWidgetStatusFilter
    | readonly OrdersWidgetStatusFilter[];
  isScoped?: boolean;
}

export const useOrdersFiltersController = ({
  defaultStatus = "all",
  isScoped = false,
}: UseOrdersFiltersControllerParams = {}) => {
  const [draftFilters, setDraftFilters] = useState<OrdersWidgetFilters>(() =>
    createDefaultOrdersFilters(defaultStatus),
  );
  const [appliedFilters, setAppliedFilters] = useState<OrdersWidgetFilters>(
    () => createDefaultOrdersFilters(defaultStatus),
  );
  // Local query until select/clear. AssetSearch is equity/crypto; apply is exact `symbols`.
  const [symbolQuery, setSymbolQuery] = useState("");
  const [filtersFormKey, setFiltersFormKey] = useState(0);

  const validationError = getOrdersFiltersValidationError(draftFilters);
  const dateParams = getOrdersFilterDateParams(appliedFilters);
  const symbols = useMemo(
    () =>
      isScoped
        ? undefined
        : appliedFilters.symbol
          ? [appliedFilters.symbol]
          : undefined,
    [appliedFilters.symbol, isScoped],
  );

  const handleFiltersChange = useCallback(
    (nextFilters: OrdersWidgetFilters) => {
      setDraftFilters(nextFilters);
      if (getOrdersFiltersValidationError(nextFilters)) {
        return;
      }
      setAppliedFilters(normalizeOrdersFiltersForApplication(nextFilters));
    },
    [],
  );

  const handleSymbolQueryChange = useCallback(
    (query: string) => {
      setSymbolQuery(query);
      if (!query.trim() && draftFilters.symbol) {
        handleFiltersChange({ ...draftFilters, symbol: "" });
      }
    },
    [draftFilters, handleFiltersChange],
  );

  const handleSymbolSelect = useCallback(
    (symbol: string) => {
      const next = symbol.trim();
      if (!next) {
        return;
      }
      setSymbolQuery(next);
      handleFiltersChange({ ...draftFilters, symbol: next });
    },
    [draftFilters, handleFiltersChange],
  );

  const handleReset = useCallback(() => {
    const nextFilters = createDefaultOrdersFilters(defaultStatus);
    setSymbolQuery("");
    setDraftFilters(nextFilters);
    setAppliedFilters(normalizeOrdersFiltersForApplication(nextFilters));
    setFiltersFormKey((key) => key + 1);
  }, [defaultStatus]);

  const handleStatusTabChange = useCallback((status: OrdersWidgetStatusTab) => {
    const apply = (current: OrdersWidgetFilters): OrdersWidgetFilters => {
      if (getSelectedStatusTab(current.status) === status) {
        return current;
      }
      return {
        ...current,
        status: setOrdersStatusTab(current.status, status),
      };
    };
    setDraftFilters(apply);
    setAppliedFilters(apply);
  }, []);

  return {
    filters: draftFilters,
    formKey: filtersFormKey,
    validationError,
    onFiltersChange: handleFiltersChange,
    onStatusTabChange: handleStatusTabChange,
    onReset: handleReset,
    symbols,
    status: toAtdOrdersStatusParam(appliedFilters.status),
    after: dateParams.after,
    until: dateParams.until,
    timeWindow: appliedFilters.timeWindow,
    assetClass:
      appliedFilters.assetClass === "all"
        ? undefined
        : appliedFilters.assetClass,
    orderType:
      appliedFilters.orderType === "all" ? undefined : appliedFilters.orderType,
    direction: appliedFilters.direction,
    appliedFilters,
    hasAppliedFilters: hasAppliedOrdersFilters(appliedFilters, defaultStatus),
    appliedTimeWindow: formatOrdersTimeWindow(appliedFilters),
    symbolQuery,
    onSymbolQueryChange: handleSymbolQueryChange,
    onSymbolSelect: handleSymbolSelect,
  };
};
